// ─── SectionRenderer ──────────────────────────────────────────
// Maps section type + variant to the correct React component.
// This is the central registry. Add new sections here.

import React from 'react';
import HeroSection from '../sections/hero/HeroSection';
import StorySection from '../sections/story/StorySection';
import EventDetailsSection from '../sections/details/EventDetailsSection';
import CountdownSection from '../sections/countdown/CountdownSection';
import TimelineSection from '../sections/timeline/TimelineSection';
import GallerySection from '../sections/gallery/GallerySection';
import VenueSection from '../sections/venue/VenueSection';
import RsvpSection from '../sections/rsvp/RsvpSection';
import FaqSection from '../sections/faq/FaqSection';
import QuoteSection from '../sections/quote/QuoteSection';
import FooterSection from '../sections/footer/FooterSection';
import SlideshowSection from '../sections/slideshow/SlideshowSection';
import CollageCanvas from '../collage/CollageCanvas';
import SectionV1CollageCanvas from './SectionV1CollageCanvas';
import { ErrorBoundary } from './ErrorBoundary';

const SECTION_REGISTRY = {
  'hero': HeroSection,
  'story': StorySection,
  'event-details': EventDetailsSection,
  'countdown': CountdownSection,
  'timeline': TimelineSection,
  'gallery': GallerySection,
  'venue': VenueSection,
  'rsvp': RsvpSection,
  'faq': FaqSection,
  'quote': QuoteSection,
  'footer': FooterSection,
  'collage': CollageCanvas,
  'slideshow': SlideshowSection,
};

/**
 * Renders a single section by looking up its type in the registry.
 * Phase 1 section-v1: when `layoutVersion === 'section-v1'` AND this is a
 * collage section, swap in `SectionV1CollageCanvas`. Any render error inside
 * the v1 canvas falls back to the legacy `CollageCanvas` so the public page
 * never breaks.
 */
export default function SectionRenderer({
  section,
  data,
  theme,
  settings,
  rsvpRef,
  globalBgActive,
  inlineWidgets,
  layoutVersion,
  isEditing = false,   // Motion 1.0 — forwarded to SectionV1CollageCanvas
}) {
  if (!section.visible) return null;

  // Section-v1: collage gets the new renderer, with legacy fallback on error.
  if (layoutVersion === 'section-v1' && section.type === 'collage') {
    return (
      <ErrorBoundary
        fallback={
          <CollageCanvas
            section={section}
            data={data}
            theme={theme}
            settings={settings}
            rsvpRef={rsvpRef}
            globalBgActive={globalBgActive}
            inlineWidgets={inlineWidgets}
          />
        }
      >
        <SectionV1CollageCanvas
          section={section}
          data={data}
          theme={theme}
          settings={settings}
          rsvpRef={rsvpRef}
          globalBgActive={globalBgActive}
          inlineWidgets={inlineWidgets}
          isEditing={isEditing}
        />
      </ErrorBoundary>
    );
  }

  const Component = SECTION_REGISTRY[section.type];
  if (!Component) {
    console.warn(`Unknown section type: ${section.type}`);
    return null;
  }

  return (
    <Component
      section={section}
      data={data}
      theme={theme}
      settings={settings}
      rsvpRef={rsvpRef}
      globalBgActive={globalBgActive}
      inlineWidgets={inlineWidgets}
    />
  );
}
